import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';

import { listBootcamps, deleteBootcamp } from '../../api/bootcampApi';
import Badge     from '../../components/ui/Badge';
import Spinner   from '../../components/ui/Spinner';
import Card      from '../../components/ui/Card';
import ConfirmModal from '../../components/ui/ConfirmModal';

const STATUS_VARIANTS = {
  active:    'success',
  draft:     'warning',
  completed: 'primary',
  archived:  'default',
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function BootcampListPage() {
  const navigate = useNavigate();

  const [bootcamps, setBootcamps] = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [status, setStatus]       = useState('');
  const [page, setPage]           = useState(1);
  const perPage = 12;

  const [deleting, setDeleting]   = useState(null); // bootcamp to delete
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchBootcamps = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listBootcamps({ search, status, page, per_page: perPage });
      const d   = res.data;
      setBootcamps(d.data ?? []);
      setTotal(d.total ?? 0);
    } catch {
      toast.error('Failed to load bootcamps');
    } finally {
      setLoading(false);
    }
  }, [search, status, page]);

  useEffect(() => { fetchBootcamps(); }, [fetchBootcamps]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, status]);

  const openDelete = (b) => { setDeleting(b); setConfirmOpen(true); };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteBootcamp(deleting.bootcamp_id ?? deleting.id);
      toast.success('Bootcamp archived.');
      setConfirmOpen(false);
      setDeleting(null);
      fetchBootcamps();
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Failed to archive bootcamp');
    }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading text-neutral-900">Bootcamps</h1>
          <p className="mt-0.5 text-sm text-neutral-500">{total} bootcamp{total !== 1 ? 's' : ''}</p>
        </div>
        <Link
          to="/bootcamps/new"
          className="inline-flex items-center gap-2 rounded-button bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-button hover:bg-primary-700 transition"
        >
          <PlusIcon className="h-4 w-4" />
          New Bootcamp
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            placeholder="Search bootcamps…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-base pl-9 text-sm"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="input-base text-sm w-full sm:w-40"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" className="text-primary-600" />
        </div>
      ) : bootcamps.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center gap-4 py-16">
            <RocketLaunchIcon className="h-12 w-12 text-neutral-300" />
            <div className="text-center">
              <p className="text-sm font-medium text-neutral-600">No bootcamps found</p>
              <p className="mt-1 text-xs text-neutral-400">
                {search || status ? 'Try adjusting your filters.' : 'Create your first bootcamp to get started.'}
              </p>
            </div>
            {!search && !status && (
              <Link to="/bootcamps/new" className="btn-primary text-sm">
                <PlusIcon className="h-4 w-4" />
                New Bootcamp
              </Link>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {bootcamps.map((b) => {
            const bid = b.bootcamp_id ?? b.id;
            return (
              <div
                key={bid}
                className="flex flex-col rounded-card border border-neutral-200 bg-white p-5 shadow-card hover:shadow-card-hover transition-shadow"
              >
                {/* Title row */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-semibold text-neutral-900 font-heading">
                      {b.title}
                    </h2>
                    {b.cohort_name && (
                      <p className="mt-0.5 truncate text-xs text-neutral-500">{b.cohort_name}</p>
                    )}
                  </div>
                  <Badge variant={STATUS_VARIANTS[b.status] ?? 'default'} className="shrink-0 capitalize">
                    {b.status}
                  </Badge>
                </div>

                {/* Description */}
                {b.description && (
                  <p className="mt-2 line-clamp-2 text-xs text-neutral-500 leading-relaxed">
                    {b.description}
                  </p>
                )}

                {/* Dates */}
                <div className="mt-3 flex items-center gap-4 text-xs text-neutral-400">
                  {b.start_date && <span>Start: {fmtDate(b.start_date)}</span>}
                  {b.end_date   && <span>End: {fmtDate(b.end_date)}</span>}
                </div>

                {/* Actions */}
                <div className="mt-4 flex items-center justify-end gap-2 border-t border-neutral-100 pt-3">
                  <button
                    onClick={() => navigate(`/bootcamps/${bid}/edit`)}
                    className="inline-flex items-center gap-1.5 rounded-button px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-100 transition"
                  >
                    <PencilSquareIcon className="h-3.5 w-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => openDelete(b)}
                    className="inline-flex items-center gap-1.5 rounded-button px-3 py-1.5 text-xs font-medium text-error-600 hover:bg-error-50 transition"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                    Archive
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-button border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 transition"
          >
            ← Prev
          </button>
          <span className="text-sm text-neutral-500">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="rounded-button border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 transition"
          >
            Next →
          </button>
        </div>
      )}

      {/* Delete confirm */}
      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Archive Bootcamp"
        message={`Are you sure you want to archive "${deleting?.title}"? This will hide it from active lists.`}
        confirmLabel="Archive"
        confirmVariant="danger"
      />
    </div>
  );
}
